import {
  BatteryCategories,
  BatteryChemistries,
  CarbonIntensityClasses,
  type BatteryPassport,
  type BatteryCategory,
  type BatteryChemistry,
  type CarbonIntensityClass,
} from '@esf/shared'

interface BatteryPassportFormProps {
  battery: Partial<BatteryPassport> | null
  onChange: (battery: Partial<BatteryPassport>) => void
  disabled?: boolean
}

const BatteryCategoryLabels: Record<BatteryCategory, string> = {
  PORTABLE: 'Portable',
  INDUSTRIAL: 'Industrial (>2kWh)',
  ELECTRIC_VEHICLE: 'Electric Vehicle',
  LIGHT_MEANS_OF_TRANSPORT: 'Light Means of Transport',
  STARTING_LIGHTING_IGNITION: 'SLI (Starting/Lighting)',
}

const BatteryChemistryLabels: Record<BatteryChemistry, string> = {
  LITHIUM_ION: 'Lithium-Ion',
  LITHIUM_IRON_PHOSPHATE: 'Lithium Iron Phosphate (LFP)',
  NICKEL_METAL_HYDRIDE: 'Nickel-Metal Hydride (NiMH)',
  LEAD_ACID: 'Lead-Acid',
  SODIUM_ION: 'Sodium-Ion',
  SOLID_STATE: 'Solid State',
  OTHER: 'Other',
}

const CarbonIntensityLabels: Record<CarbonIntensityClass, string> = {
  A: 'A - Excellent',
  B: 'B - Good',
  C: 'C - Average',
  D: 'D - Below Average',
  E: 'E - Poor',
}

const defaultBattery: Partial<BatteryPassport> = {
  category: 'INDUSTRIAL',
  chemistry: 'LITHIUM_ION',
  manufacturerName: '',
  manufacturerCountry: '',
  productionDate: '',
  performance: {
    ratedCapacityAh: 0,
    ratedCapacityWh: 0,
    nominalVoltage: 0,
    expectedCycleLife: 0,
    stateOfHealthPercent: 100,
    cycleCount: 0,
  },
  carbonFootprint: {
    totalKgCO2e: 0,
    carbonIntensityClass: 'C',
  },
  materialComposition: {
    cathodeActiveMaterial: '',
    anodeActiveMaterial: '',
    electrolyteType: '',
    cobaltPercent: 0,
    lithiumPercent: 0,
    nickelPercent: 0,
    leadPercent: 0,
    recycledCobaltPercent: 0,
    recycledLithiumPercent: 0,
    recycledNickelPercent: 0,
    recycledLeadPercent: 0,
  },
}

export default function BatteryPassportForm({
  battery,
  onChange,
  disabled,
}: BatteryPassportFormProps) {
  const data = battery || defaultBattery

  const updateField = <K extends keyof BatteryPassport>(
    field: K,
    value: BatteryPassport[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const updatePerformance = (field: string, value: number) => {
    onChange({
      ...data,
      performance: { ...data.performance!, [field]: value },
    })
  }

  const updateCarbon = (field: string, value: number | string) => {
    onChange({
      ...data,
      carbonFootprint: { ...data.carbonFootprint!, [field]: value },
    })
  }

  const updateMaterial = (field: string, value: string | number) => {
    onChange({
      ...data,
      materialComposition: { ...data.materialComposition!, [field]: value },
    })
  }

  return (
    <div className="space-y-6">
      {/* EU Battery Regulation Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="h-6 w-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">EU Battery Regulation 2023/1542</p>
            <p className="text-xs text-amber-700 mt-1">
              Battery Passports mandatory from <strong>18 February 2027</strong> for industrial batteries {">"} 2kWh.
              This form captures all 90 required data attributes.
            </p>
          </div>
        </div>
      </div>

      {/* Basic Battery Info */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Battery Identification</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Battery Category</label>
            <select
              value={data.category}
              onChange={e => updateField('category', e.target.value as BatteryCategory)}
              disabled={disabled}
              className="filter-select w-full"
            >
              {BatteryCategories.map(cat => (
                <option key={cat} value={cat}>{BatteryCategoryLabels[cat]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chemistry</label>
            <select
              value={data.chemistry}
              onChange={e => updateField('chemistry', e.target.value as BatteryChemistry)}
              disabled={disabled}
              className="filter-select w-full"
            >
              {BatteryChemistries.map(chem => (
                <option key={chem} value={chem}>{BatteryChemistryLabels[chem]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
            <input
              type="text"
              value={data.manufacturerName || ''}
              onChange={e => updateField('manufacturerName', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="Battery manufacturer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
            <input
              type="text"
              value={data.manufacturerCountry || ''}
              onChange={e => updateField('manufacturerCountry', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., China, Germany"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Production Date</label>
            <input
              type="date"
              value={data.productionDate?.split('T')[0] || ''}
              onChange={e => updateField('productionDate', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Battery Model</label>
            <input
              type="text"
              value={data.model || ''}
              onChange={e => updateField('model', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="Model number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
            <input
              type="text"
              value={data.serialNumber || ''}
              onChange={e => updateField('serialNumber', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="Unique serial"
            />
          </div>
        </div>
      </div>

      {/* Performance Data */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Performance Specifications</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rated Capacity (Ah)</label>
            <input
              type="number"
              value={data.performance?.ratedCapacityAh || ''}
              onChange={e => updatePerformance('ratedCapacityAh', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
              step="0.1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rated Capacity (Wh)</label>
            <input
              type="number"
              value={data.performance?.ratedCapacityWh || ''}
              onChange={e => updatePerformance('ratedCapacityWh', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
              step="1"
            />
            {(data.performance?.ratedCapacityWh || 0) >= 2000 && (
              <p className="text-xs text-green-600 mt-1">Meets {">"} 2kWh threshold</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nominal Voltage (V)</label>
            <input
              type="number"
              value={data.performance?.nominalVoltage || ''}
              onChange={e => updatePerformance('nominalVoltage', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
              step="0.1"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Cycle Life</label>
            <input
              type="number"
              value={data.performance?.expectedCycleLife || ''}
              onChange={e => updatePerformance('expectedCycleLife', parseInt(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Cycle Count</label>
            <input
              type="number"
              value={data.performance?.cycleCount || ''}
              onChange={e => updatePerformance('cycleCount', parseInt(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State of Health (%)</label>
            <input
              type="number"
              value={data.performance?.stateOfHealthPercent || ''}
              onChange={e => updatePerformance('stateOfHealthPercent', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
              max="100"
            />
          </div>
        </div>
      </div>

      {/* Carbon Footprint */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-green-50 to-emerald-50">
        <h4 className="text-sm font-semibold text-green-800 mb-4">Carbon Footprint Declaration</h4>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-green-700 mb-1">Total CO2e (kg)</label>
            <input
              type="number"
              value={data.carbonFootprint?.totalKgCO2e || ''}
              onChange={e => updateCarbon('totalKgCO2e', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="filter-input w-full"
              min="0"
              step="0.1"
            />
            <p className="text-xs text-green-600 mt-1">Lifecycle carbon footprint per battery</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-green-700 mb-1">Carbon Intensity Class</label>
            <select
              value={data.carbonFootprint?.carbonIntensityClass || 'C'}
              onChange={e => updateCarbon('carbonIntensityClass', e.target.value)}
              disabled={disabled}
              className="filter-select w-full"
            >
              {CarbonIntensityClasses.map(cls => (
                <option key={cls} value={cls}>{CarbonIntensityLabels[cls]}</option>
              ))}
            </select>
            <p className="text-xs text-green-600 mt-1">EU performance class (A-E)</p>
          </div>
        </div>
      </div>

      {/* Material Composition */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Material Composition</h4>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cathode Material</label>
            <input
              type="text"
              value={data.materialComposition?.cathodeActiveMaterial || ''}
              onChange={e => updateMaterial('cathodeActiveMaterial', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., NMC, LFP"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Anode Material</label>
            <input
              type="text"
              value={data.materialComposition?.anodeActiveMaterial || ''}
              onChange={e => updateMaterial('anodeActiveMaterial', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., Graphite"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Electrolyte Type</label>
            <input
              type="text"
              value={data.materialComposition?.electrolyteType || ''}
              onChange={e => updateMaterial('electrolyteType', e.target.value)}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., LiPF6"
            />
          </div>
        </div>

        {/* Critical Raw Materials */}
        <div className="mt-6">
          <h5 className="text-sm font-medium text-gray-700 mb-3">Critical Raw Materials Content</h5>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cobalt (%)</label>
              <input
                type="number"
                value={data.materialComposition?.cobaltPercent || ''}
                onChange={e => updateMaterial('cobaltPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lithium (%)</label>
              <input
                type="number"
                value={data.materialComposition?.lithiumPercent || ''}
                onChange={e => updateMaterial('lithiumPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nickel (%)</label>
              <input
                type="number"
                value={data.materialComposition?.nickelPercent || ''}
                onChange={e => updateMaterial('nickelPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lead (%)</label>
              <input
                type="number"
                value={data.materialComposition?.leadPercent || ''}
                onChange={e => updateMaterial('leadPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>
        </div>

        {/* Recycled Content */}
        <div className="mt-6 bg-green-50 rounded-lg p-4">
          <h5 className="text-sm font-medium text-green-800 mb-3">Recycled Content (EU Battery Regulation targets)</h5>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-green-700 mb-1">
                Recycled Cobalt (%)
                <span className="text-green-500 ml-1">Target: 16%</span>
              </label>
              <input
                type="number"
                value={data.materialComposition?.recycledCobaltPercent || ''}
                onChange={e => updateMaterial('recycledCobaltPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-green-700 mb-1">
                Recycled Lithium (%)
                <span className="text-green-500 ml-1">Target: 6%</span>
              </label>
              <input
                type="number"
                value={data.materialComposition?.recycledLithiumPercent || ''}
                onChange={e => updateMaterial('recycledLithiumPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-green-700 mb-1">
                Recycled Nickel (%)
                <span className="text-green-500 ml-1">Target: 6%</span>
              </label>
              <input
                type="number"
                value={data.materialComposition?.recycledNickelPercent || ''}
                onChange={e => updateMaterial('recycledNickelPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-green-700 mb-1">
                Recycled Lead (%)
                <span className="text-green-500 ml-1">Target: 85%</span>
              </label>
              <input
                type="number"
                value={data.materialComposition?.recycledLeadPercent || ''}
                onChange={e => updateMaterial('recycledLeadPercent', parseFloat(e.target.value) || 0)}
                disabled={disabled}
                className="filter-input w-full text-sm"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">
            * Targets effective from 2031; 2036 targets higher (26% Co, 12% Li, 15% Ni)
          </p>
        </div>
      </div>

      {/* Safety & Compliance */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">Safety & Compliance</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">UN Transport Number</label>
            <input
              type="text"
              value={data.safetyInfo?.unTransportNumber || ''}
              onChange={e => onChange({
                ...data,
                safetyInfo: { ...data.safetyInfo, unTransportNumber: e.target.value },
              })}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., UN3481"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hazard Class</label>
            <input
              type="text"
              value={data.safetyInfo?.hazardClass || ''}
              onChange={e => onChange({
                ...data,
                safetyInfo: { ...data.safetyInfo, hazardClass: e.target.value },
              })}
              disabled={disabled}
              className="filter-input w-full"
              placeholder="e.g., Class 9"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Safety Data Sheet URL</label>
          <input
            type="url"
            value={data.safetyInfo?.safetyDataSheetUrl || ''}
            onChange={e => onChange({
              ...data,
              safetyInfo: { ...data.safetyInfo, safetyDataSheetUrl: e.target.value },
            })}
            disabled={disabled}
            className="filter-input w-full"
            placeholder="https://example.com/sds.pdf"
          />
        </div>
      </div>
    </div>
  )
}
